import { X } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import type { VoiceNoteErrorMessage } from '../../../lib/use-voice-note';
import { Button, IconButton } from '../ui';
import styles from './ComposerPill.module.css';

// Why a voice note didn't happen — a blocked mic, a browser that can't record, a capture that
// produced nothing, or an upload that failed. It replaces the composer's control row for as long
// as it is up, and NEVER a modal: the phones show an alert here, but a browser's own permission
// prompt is already a modal and stacking a second dialog behind it is what makes a denied mic feel
// like a broken app. Dismiss restores the composer.
//
// A FAILED SEND is the one recoverable case, so it is the only one with a Retry: the recording
// still exists (the upload failed, not the capture), and making someone say it all again because
// the network blinked is the wrong answer. Dismiss on that case throws the take away, which is
// why it is the secondary action and not the only one.
export function VoiceNoteNotice({
  error,
  onDismiss,
  onRetry,
}: {
  error: VoiceNoteErrorMessage;
  onDismiss: () => void;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  const message =
    error === 'denied'
      ? t('chat.voice.micDenied')
      : error === 'unsupported'
        ? t('chat.voice.notSupported')
        : error === 'send-failed'
          ? t('chat.voice.sendFailed')
          : t('chat.voice.recordingFailed');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <span role="status" style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--ds-text-destructive)' }}>
        {message}
      </span>
      {error === 'send-failed' && onRetry && (
        <Button size="small" variant="outlined" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
      <IconButton size="small" variant="text" onClick={onDismiss} aria-label={t('common.close')} title={t('common.close')} className={styles.chatRound}>
        <X size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </IconButton>
    </div>
  );
}
